import {
	AfterViewInit,
	Component,
	ElementRef,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	ViewChild
} from '@angular/core';
import {FlowSpec} from './email-flow-specs';

const SVG_NS = 'http://www.w3.org/2000/svg';

const ICONS = {
	sender: '<circle cx="12" cy="8.5" r="3.4" fill="none" stroke="CUR" stroke-width="1.7"/><path d="M5.5 19c.8-3.4 3.4-5 6.5-5s5.7 1.6 6.5 5" fill="none" stroke="CUR" stroke-width="1.7" stroke-linecap="round"/>',
	globe: '<circle cx="12" cy="12" r="8.4" fill="none" stroke="CUR" stroke-width="1.6"/><path d="M3.6 12h16.8M12 3.6c-5.6 5-5.6 11.8 0 16.8M12 3.6c5.6 5 5.6 11.8 0 16.8" fill="none" stroke="CUR" stroke-width="1.3"/>',
	shield: '<path d="M12 3l7.2 2.6v5.2c0 4.6-3 8.3-7.2 9.9-4.2-1.6-7.2-5.3-7.2-9.9V5.6L12 3z" fill="none" stroke="CUR" stroke-width="1.7" stroke-linejoin="round"/><path d="M8.7 12l2.3 2.4 4.3-4.6" fill="none" stroke="CUR" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
	ms: '<rect x="4" y="4" width="7.3" height="7.3" fill="#f25022"/><rect x="12.7" y="4" width="7.3" height="7.3" fill="#7fba00"/><rect x="4" y="12.7" width="7.3" height="7.3" fill="#00a4ef"/><rect x="12.7" y="12.7" width="7.3" height="7.3" fill="#ffb900"/>',
	google: '<path d="M21 12.2c0-.7-.06-1.2-.18-1.8H12v3.5h5.1c-.1.9-.66 2.2-1.9 3l2.9 2.3c1.8-1.6 2.9-4.1 2.9-7z" fill="#4285f4"/><path d="M12 21c2.4 0 4.5-.8 6-2.2l-2.9-2.3c-.8.6-1.8.9-3.1.9-2.4 0-4.4-1.6-5.1-3.8l-3 2.3C5.4 19 8.5 21 12 21z" fill="#34a853"/><path d="M6.9 13.6a5.6 5.6 0 010-3.4l-3-2.3a9 9 0 000 8z" fill="#fbbc05"/><path d="M12 6.5c1.7 0 2.9.7 3.5 1.3l2.6-2.5C16.5 3.8 14.4 3 12 3 8.5 3 5.4 5 3.9 7.9l3 2.3c.7-2.1 2.7-3.7 5.1-3.7z" fill="#ea4335"/>',
	server: '<rect x="4.5" y="4.5" width="15" height="6" rx="1.4" fill="none" stroke="CUR" stroke-width="1.6"/><rect x="4.5" y="13.5" width="15" height="6" rx="1.4" fill="none" stroke="CUR" stroke-width="1.6"/><circle cx="8" cy="7.5" r="1" fill="CUR"/><circle cx="8" cy="16.5" r="1" fill="CUR"/>',
	mailbox: '<rect x="3.6" y="6" width="16.8" height="12" rx="1.8" fill="none" stroke="CUR" stroke-width="1.7"/><path d="M4.5 7.5l7.5 6 7.5-6" fill="none" stroke="CUR" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
	quarantine: '<rect x="4.5" y="8" width="15" height="11" rx="1.6" fill="none" stroke="CUR" stroke-width="1.6"/><path d="M9 8V6.4A2.4 2.4 0 0111.4 4h1.2A2.4 2.4 0 0115 6.4V8" fill="none" stroke="CUR" stroke-width="1.6"/><path d="M12 11.5v3.4M12 17.2v.2" stroke="CUR" stroke-width="1.7" stroke-linecap="round"/>',
	cloud: '<path d="M7 17.5a4 4 0 01-.4-8A5.4 5.4 0 0117 8.6a4.4 4.4 0 01-.6 8.9H7z" fill="none" stroke="CUR" stroke-width="1.6" stroke-linejoin="round"/>',
	api: '<path d="M8.5 7L4 12l4.5 5M15.5 7L20 12l-4.5 5" fill="none" stroke="CUR" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.2 5.5l-2.4 13" stroke="CUR" stroke-width="1.6" stroke-linecap="round"/>'
};

/* CSS variables do not resolve inside SVG presentation attributes, so the plan's
   primary color (which follows the Look and Feel customization) is resolved at render time. */
function resolvePrimaryColors(): {primary: string, primarySoft: string} {
	const style = getComputedStyle(document.body);
	const h = style.getPropertyValue('--bs-primary-h').trim();
	const s = style.getPropertyValue('--bs-primary-s').trim();
	const l = style.getPropertyValue('--bs-primary-l').trim();
	if (h && s && l) {
		return {primary: `hsl(${h}, ${s}, ${l})`, primarySoft: `hsl(${h}, 60%, 97%)`};
	}
	return {primary: '#486db5', primarySoft: '#f2f6fd'};
}

function buildTones(primary: string, primarySoft: string) {
	return {
		tones: {
			default: {stroke: '#93a0b4', icon: '#5a6675', bg: '#ffffff'},
			trustifi: {stroke: primary, icon: primary, bg: primarySoft},
			brand: {stroke: '#b8c2d2', icon: '#5a6675', bg: '#ffffff'},
			aws: {stroke: '#c48a2d', icon: '#b07716', bg: '#fdf8ef'},
			danger: {stroke: '#d38893', icon: '#c22e3e', bg: '#fdf4f5'},
			success: {stroke: '#67a888', icon: '#198754', bg: '#f2faf6'}
		},
		edgeTones: {
			primary: primary,
			success: '#198754',
			danger: '#c22e3e',
			muted: '#9aa5b4'
		}
	};
}

let uidCounter = 0;

@Component({
	selector: 'flow-diagram-c',
	template: '<div #host class="w-100"></div>',
})
export class FlowDiagramComponent implements OnChanges, AfterViewInit, OnDestroy {
	@Input() spec: FlowSpec;
	@ViewChild('host', {static: true}) host: ElementRef;

	private viewReady = false;
	private resizeTimer: any;
	private lastSizeBucket = '';

	ngAfterViewInit() {
		this.viewReady = true;
		this.render();
	}

	ngOnChanges() {
		if (this.viewReady) {
			this.render();
		}
	}

	// labels shrink on narrow screens; re-render only when the size bucket actually changes
	@HostListener('window:resize')
	onResize() {
		clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(() => {
			if (this.viewReady && this.sizeBucket() !== this.lastSizeBucket) {
				this.render();
			}
		}, 150);
	}

	ngOnDestroy() {
		clearTimeout(this.resizeTimer);
	}

	private sizeBucket(): string {
		const w = this.host?.nativeElement?.clientWidth || 1060;
		return w < 700 ? 'xs' : w < 1000 ? 'sm' : 'lg';
	}

	private el(name: string, attrs: any, parent?: Element): SVGElement {
		const n = document.createElementNS(SVG_NS, name) as SVGElement;
		for (const k in attrs) {
			if (attrs.hasOwnProperty(k)) {
				n.setAttribute(k, attrs[k]);
			}
		}
		if (parent) {
			parent.appendChild(n);
		}
		return n;
	}

	private anchor(node, side: string): number[] {
		const {x, y, w, h} = node;
		switch (side) {
			case 'left': return [x, y + h / 2];
			case 'right': return [x + w, y + h / 2];
			case 'top': return [x + w / 2, y];
			default: return [x + w / 2, y + h];
		}
	}

	private edgePath(a: number[], b: number[], sideA: string, sideB: string, elbow?: boolean): string {
		const [x1, y1] = a, [x2, y2] = b;
		if (elbow) {
			// CC: L-shaped 90° path — go in sideA direction first, then turn toward the target
			if (sideA === 'top' || sideA === 'bottom') {
				return `M ${x1} ${y1} L ${x1} ${y2} L ${x2} ${y2}`;
			}
			return `M ${x1} ${y1} L ${x2} ${y1} L ${x2} ${y2}`;
		}
		const horiz = (sideA === 'left' || sideA === 'right');
		const k = horiz ? Math.max(40, Math.abs(x2 - x1) * 0.42) : Math.max(34, Math.abs(y2 - y1) * 0.5);
		const c1 = sideA === 'right' ? [x1 + k, y1] : sideA === 'left' ? [x1 - k, y1] : sideA === 'bottom' ? [x1, y1 + k] : [x1, y1 - k];
		const c2 = sideB === 'left' ? [x2 - k, y2] : sideB === 'right' ? [x2 + k, y2] : sideB === 'top' ? [x2, y2 - k] : [x2, y2 + k];
		return `M ${x1} ${y1} C ${c1} ${c2} ${x2} ${y2}`;
	}

	private render() {
		const hostEl = this.host?.nativeElement;
		if (!hostEl) {
			return;
		}
		hostEl.innerHTML = '';
		if (!this.spec) {
			return;
		}
		const spec = this.spec;
		const uid = ++uidCounter;
		const {primary, primarySoft} = resolvePrimaryColors();
		const {tones: TONES, edgeTones: EDGE_TONES} = buildTones(primary, primarySoft);

		// smaller edge labels on narrow screens so they don't crowd the nodes
		this.lastSizeBucket = this.sizeBucket();
		const labelFont = this.lastSizeBucket === 'xs' ? 8 : this.lastSizeBucket === 'sm' ? 9 : 9.5;
		const stepFont = this.lastSizeBucket === 'xs' ? 9.5 : 10.5;
		const stepRadius = this.lastSizeBucket === 'xs' ? 9 : 10;

		const svg = this.el('svg', {
			viewBox: `0 0 1060 ${spec.height || 320}`, role: 'img', 'aria-label': spec.aria || 'Email flow diagram',
			style: 'display:block;width:100%;height:auto;'
		});
		const defs = this.el('defs', {}, svg);
		for (const t in EDGE_TONES) {
			if (EDGE_TONES.hasOwnProperty(t)) {
				const m = this.el('marker', {id: `flow-arr-${t}-${uid}`, viewBox: '0 0 10 10', refX: '8', refY: '5',
					markerWidth: '7', markerHeight: '7', orient: 'auto-start-reverse'}, defs);
				this.el('path', {d: 'M0 0L10 5L0 10z', fill: EDGE_TONES[t]}, m);
			}
		}

		const nodeMap = {};
		spec.nodes.forEach(n => {
			const node = {...n, w: n.w || 158, h: n.h || 92};
			if (n.tags?.length) {
				node.h += 30;
			}
			nodeMap[node.id] = node;
		});

		const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		const edgeLayer = this.el('g', {}, svg);
		const pendingLabels = [];

		spec.edges.forEach((e, i) => {
			const a = this.anchor(nodeMap[e.from], e.fromSide || 'right');
			const b = this.anchor(nodeMap[e.to], e.toSide || 'left');
			const d = this.edgePath(a, b, e.fromSide || 'right', e.toSide || 'left', e.elbow);
			const color = EDGE_TONES[e.tone || 'primary'];
			const pid = `flow-p${uid}-${i}`;
			const p = this.el('path', {id: pid, d, fill: 'none', stroke: color, 'stroke-width': 2,
				'marker-end': `url(#flow-arr-${e.tone || 'primary'}-${uid})`,
				'stroke-dasharray': e.dashed ? '5 5' : 'none', opacity: e.dashed ? .85 : 1}, edgeLayer);

			if (!reducedMotion) {
				const dots = e.dashed ? 0 : e.dots === 0 ? 0 : (e.dots || 1);
				for (let dI = 0; dI < dots; dI++) {
					const c = this.el('circle', {r: 2.5, fill: color, opacity: .65}, edgeLayer);
					const dur = (e.dur || 3.2) * 2.5;
					const am = this.el('animateMotion', {dur: dur + 's', repeatCount: 'indefinite',
						begin: (dI * (dur / dots) + (e.delay || 0)) + 's'}, c);
					this.el('mpath', {href: '#' + pid}, am);
				}
			}
			pendingLabels.push({path: p, edge: e, a, b});
		});

		// nodes above edges
		spec.nodes.forEach(rawNode => {
			const n = nodeMap[rawNode.id];
			const tone = TONES[n.tone || 'default'];
			const g = this.el('g', {}, svg);
			this.el('rect', {x: n.x, y: n.y, width: n.w, height: n.h, rx: 8, fill: tone.bg,
				stroke: tone.stroke, 'stroke-width': n.tone === 'trustifi' ? 2 : 1.5}, g);
			const ig = this.el('g', {transform: `translate(${n.x + n.w / 2 - 12}, ${n.y + 10})`}, g);
			ig.innerHTML = ICONS[n.icon].split('CUR').join(tone.icon);
			const titleFontSize = n.title.length > 20 ? '11' : n.title.length > 16 ? '11.5' : '12.5';
			const t1 = this.el('text', {x: n.x + n.w / 2, y: n.y + 52, 'text-anchor': 'middle', 'font-size': titleFontSize,
				'font-weight': '600', fill: '#212529'}, g);
			t1.textContent = n.title;
			if (n.sub) {
				const subFont = n.sub.length > 22 ? '8' : '9.5';
				const t2 = this.el('text', {x: n.x + n.w / 2, y: n.y + 68, 'text-anchor': 'middle', 'font-size': subFont,
					fill: '#8b95a3', 'font-family': 'ui-monospace,SF Mono,Consolas,monospace'}, g);
				t2.textContent = n.sub;
			}
			if (n.sub2) {
				const t3 = this.el('text', {x: n.x + n.w / 2, y: n.y + 80, 'text-anchor': 'middle', 'font-size': '9.5',
					fill: '#8b95a3', 'font-family': 'ui-monospace,SF Mono,Consolas,monospace'}, g);
				t3.textContent = n.sub2;
			}
			if (n.badge) {
				const bw = n.badge.length * 6.2 + 16;
				this.el('rect', {x: n.x + n.w / 2 - bw / 2, y: n.y - 11, width: bw, height: 18, rx: 9, fill: '#eaf3fb',
					stroke: '#9ec4de', 'stroke-dasharray': '3 2'}, g);
				const bt = this.el('text', {x: n.x + n.w / 2, y: n.y + 2, 'text-anchor': 'middle', 'font-size': '9.5',
					'font-weight': '700', fill: '#20618d'}, g);
				bt.textContent = n.badge;
			}
			// step badge on the node itself — for steps that happen inside a node (e.g. scanning)
			if (n.step) {
				this.el('circle', {cx: n.x, cy: n.y, r: stepRadius, fill: EDGE_TONES.primary,
					stroke: '#ffffff', 'stroke-width': '1.5'}, g);
				const st = this.el('text', {x: n.x, y: n.y + stepFont * 0.36, 'text-anchor': 'middle',
					'font-size': String(stepFont), 'font-weight': '700', fill: '#fff'}, g);
				st.textContent = String(n.step);
			}
			if (n.tags?.length) {
				// CC: scan-target section — labeled separator + pill row in the expanded area
				const baseH = n.h - 30;
				const sepY = n.y + baseH;
				const label = 'Scans:';
				const labelW = label.length * 5 + 10;
				const labelX = n.x + n.w / 2;
				this.el('line', {x1: n.x + 12, y1: sepY, x2: labelX - labelW / 2 - 4, y2: sepY,
					stroke: tone.stroke, 'stroke-width': '0.5', opacity: '0.35'}, g);
				this.el('line', {x1: labelX + labelW / 2 + 4, y1: sepY, x2: n.x + n.w - 12, y2: sepY,
					stroke: tone.stroke, 'stroke-width': '0.5', opacity: '0.35'}, g);
				const lt = this.el('text', {x: labelX, y: sepY + 1.8, 'text-anchor': 'middle',
					'font-size': '7.5', fill: tone.icon, 'font-weight': '500', opacity: '0.7'}, g);
				lt.textContent = label;
				const pillH = 14;
				const pillY = n.y + baseH + 8;
				const charW = 4.5;
				const padX = 4;
				const gap = 3;
				const pillWidths = n.tags.map(tag => Math.round(tag.length * charW + padX * 2));
				const totalW = pillWidths.reduce((s, w) => s + w, 0) + gap * (n.tags.length - 1);
				let px = n.x + (n.w - totalW) / 2;
				n.tags.forEach((tag, ti) => {
					const pw = pillWidths[ti];
					this.el('rect', {x: px, y: pillY, width: pw, height: pillH,
						rx: pillH / 2, fill: tone.bg, stroke: tone.stroke, 'stroke-width': '0.75'}, g);
					const pt = this.el('text', {x: px + pw / 2, y: pillY + 10,
						'text-anchor': 'middle', 'font-size': '8', fill: tone.icon, 'font-weight': '500'}, g);
					pt.textContent = tag;
					px += pw + gap;
				});
			}
		});

		hostEl.appendChild(svg);

		// step badges + labels need the paths measured in the DOM
		pendingLabels.forEach(({path, edge, a, b}) => {
			const color = EDGE_TONES[edge.tone || 'primary'];
			let mid = {x: (a[0] + b[0]) / 2, y: (a[1] + b[1]) / 2};
			try {
				const len = (path as SVGPathElement).getTotalLength();
				if (len) {
					const pt = (path as SVGPathElement).getPointAtLength(len * (edge.elbow ? 1 / 3 : (edge.labelAt || 0.5)));
					mid = {x: pt.x, y: pt.y};
				}
			} catch (e) { /* jsdom / detached fallback */ }
			if (edge.step) {
				this.el('circle', {cx: mid.x, cy: mid.y - 14, r: stepRadius, fill: color}, svg);
				const t = this.el('text', {x: mid.x, y: mid.y - 14 + stepFont * 0.36, 'text-anchor': 'middle', 'font-size': String(stepFont),
					'font-weight': '700', fill: '#fff'}, svg);
				t.textContent = String(edge.step);
			}
			if (edge.label) {
				const t = this.el('text', {x: mid.x, y: mid.y + 20, 'text-anchor': 'middle', 'font-size': String(labelFont),
					fill: '#5a6675', 'font-weight': '600'}, svg) as SVGTextElement;
				t.textContent = edge.label;
				// background pill so arrows and node borders never strike through the label
				try {
					const bb = t.getBBox();
					const pill = this.el('rect', {x: bb.x - 6, y: bb.y - 2.5, width: bb.width + 12, height: bb.height + 5,
						rx: (bb.height + 5) / 2, fill: '#f9fbfe', 'fill-opacity': '0.92', stroke: '#dde3ec', 'stroke-width': '1'});
					svg.insertBefore(pill, t);
				} catch (e) { /* getBBox unavailable in detached/jsdom contexts */ }
			}
		});
	}
}
